import type { RequirementBlock } from '../components/ProjectRequirements'

export type RequirementSourceChoice = { fileId: string; sectionId: string; text?: string; previewUrl?: string | null }
export type RequirementSourceFile = { id: string; name: string; parse_status: string; sections?: { id: string; location: string; text?: string }[] }
export type RequirementSourceSection = { id: string; location: string; text?: string }
export const requirementSourceKey = (fileId: string, sectionId: string) => JSON.stringify([fileId, sectionId])
export const requirementSectionPage = (section: RequirementSourceSection) => {
  const value = /^(?:slide|page)-(\d+)$/.exec(section.id)?.[1] ?? /^第\s*(\d+)\s*页/.exec(section.location)?.[1]
  const ordinal = Number(value)
  return Number.isSafeInteger(ordinal) && ordinal > 0 ? ordinal : null
}

/** Keep original page numbers even when parsing skips an empty page. Never infer Word paragraph/page correspondence. */
export function requirementSourcePages(file: { sections?: RequirementSourceSection[]; preview_pages?: { page_ref: string; ordinal: number; preview_url?: string | null }[] }) {
  const sections = new Map<number, RequirementSourceSection>()
  for (const section of file.sections ?? []) {
    const page = requirementSectionPage(section)
    if (page !== null) sections.set(page, section)
  }
  const images = new Map((file.preview_pages ?? []).map(page => [page.ordinal, page]))
  return [...new Set([...images.keys(), ...sections.keys()])].sort((a,b)=>a-b).map(ordinal => ({
    ordinal, section: sections.get(ordinal), preview_url: images.get(ordinal)?.preview_url ?? null,
  }))
}

/** Preserve saved snapshots, deduplicate by source identity and recheck current project metadata before insertion. */
export function appendRequirementSources(blocks: RequirementBlock[], files: RequirementSourceFile[], choices: RequirementSourceChoice[], newId: () => string, insertAt = blocks.length): RequirementBlock[] {
  const output = [...blocks], additions: RequirementBlock[] = [], seen = new Set(blocks.filter(block => block.kind === 'source').map(block => requirementSourceKey(block.fileId ?? '',block.sectionId ?? '')))
  for (const choice of choices) {
    if (output.length + additions.length >= 200) break
    const key = requirementSourceKey(choice.fileId,choice.sectionId)
    const file = files.find(file => file.id === choice.fileId && file.parse_status === 'success')
    const section = file?.sections?.find(section => section.id === choice.sectionId)
    if (!file || !section || seen.has(key)) continue
    additions.push({ id: newId(), kind: 'source', title: `${file.name} · ${section.location}`, text: choice.text ?? '', fileId: file.id, sectionId: section.id, fileName: file.name, location: section.location, previewUrl: choice.previewUrl ?? null })
    seen.add(key)
  }
  output.splice(Math.max(0, Math.min(insertAt, output.length)), 0, ...additions)
  return output
}
